/**
 * CareStart Studio — client read path for CareStart system prompts (Phase 1, read-only).
 *
 * WHAT: Thin wrapper over the `getCareStartPromptsForSupervisor` Cloud Function callable.
 * WHY: The browser cannot (and must not) read clinical prompt docs (`llmGlobalPrompts`) directly —
 *   those are locked to LLM managers in Firestore rules. The callable validates the Clinic Manager's
 *   role server-side and returns only the bounded CareStart `-scmap4` allowlist.
 * INVARIANT: Read-only. There is no write counterpart here; supervisor prompt overrides live on
 *   `careStartVariants` only, never mutating global clinical prompt docs. (`llmQueryPrompts` is unused.)
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { wrapFirebaseCallableErrorForUser } from '../../utils/firebaseHttpsCallableUserMessage';

const CALLABLE_NAME = 'getCareStartPromptsForSupervisor';

/**
 * Fetch the current CareStart system prompts for the Studio view.
 *
 * @returns {Promise<Array<{
 *   queryId: string,
 *   label: string,
 *   description: string,
 *   exists: boolean,
 *   prompt: string,
 * }>>} One entry per CareStart query ID (system defaults; read-only).
 * @throws {Error} A user-safe error (message suitable for display) on auth/permission/network failure.
 */
export async function fetchCareStartPrompts() {
  try {
    const callable = httpsCallable(getFunctions(), CALLABLE_NAME);
    const result = await callable({});
    const prompts = result?.data?.prompts;
    return Array.isArray(prompts) ? prompts : [];
  } catch (error) {
    console.warn('[careStartPromptReadService] fetchCareStartPrompts failed', error);
    throw wrapFirebaseCallableErrorForUser(error, { feature: 'carestart-studio' });
  }
}

const careStartPromptReadService = { fetchCareStartPrompts };
export default careStartPromptReadService;
